<template>
  <teleport to="body"><SessionExpiryAlert /></teleport>
  <section id="content"><router-view></router-view></section>
  <transition name="floating-action-swap">
    <ChatAssistant v-if="showChatAssistant" />
  </transition>
</template>

<script>
import { mapState } from "vuex";
import {
  AUTO_LOGIN_ACTION,
  IS_USER_AUTHENTICATED_GETTER,
} from "./store/storeconstants";
import SessionExpiryAlert from "./views/modal/SessionExpiryAlert.vue";
import ChatAssistant from "./components/ChatAssistant.vue";
export default {
  name: "App",
  components: { SessionExpiryAlert, ChatAssistant },
  data() {
    return {
      responsiveTableObserver: null,
      responsiveTableRefreshTimer: null,
    };
  },
  computed: {
    ...mapState({
      autoLogout: (state) => state.auth.autoLogout,
    }),
    showChatAssistant() {
      const hiddenRoutes = [
        "/login",
        "/register",
        "/resetpassword",
        "/mobile-frr",
        "/launch_field",
        "/launch_field_wall",
      ];
      const path = String(this.$route.path || "").toLowerCase();
      const isLaunchFieldWall = String(
        this.$route.query?.launchField || "",
      ).toLowerCase() === "wall";
      return (
        this.$store.getters[`auth/${IS_USER_AUTHENTICATED_GETTER}`] &&
        !this.$store.getters["ui/HAS_SAVE_FALLBACK"] &&
        !hiddenRoutes.includes(path) &&
        !isLaunchFieldWall
      );
    },
  },
  watch: {
    autoLogout(curValue, oldValue) {
      if (curValue && curValue != oldValue) {
        this.$router.replace("/Login");
      }
    },
    $route() {
      this.scheduleResponsiveTableEnhance();
    },
  },
  created() {
    this.$store.dispatch(`auth/${AUTO_LOGIN_ACTION}`);
  },
  mounted() {
    window.addEventListener("beforeunload", this._onBeforeUnload);
    this.init();
    this.$nextTick(this.setupResponsiveTables);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this._onBeforeUnload);
    if (this.responsiveTableObserver) {
      this.responsiveTableObserver.disconnect();
    }
    if (this.responsiveTableRefreshTimer) {
      window.clearTimeout(this.responsiveTableRefreshTimer);
    }
  },
  methods: {
    init() {
      const browserPath = window.location.pathname;
      const hasExplicitRoute =
        (this.$route.path && this.$route.path !== "/") ||
        (browserPath && browserPath !== "/");
      const hasQuery =
        Object.keys(this.$route.query || {}).length > 0 ||
        window.location.search.length > 1;

      if (!hasExplicitRoute && !hasQuery) {
        this.$router.replace("home");
      }
    },
    _onBeforeUnload(e) {
      if (!this.$store.getters["ui/HAS_UNSAVED_CHANGES"]) return;
      e.preventDefault();
      e.returnValue = "";
    },
    setupResponsiveTables() {
      this.decorateResponsiveTables();

      const content = document.getElementById("content");
      if (!content || typeof MutationObserver === "undefined") return;

      if (this.responsiveTableObserver) {
        this.responsiveTableObserver.disconnect();
      }

      this.responsiveTableObserver = new MutationObserver(() => {
        this.scheduleResponsiveTableEnhance();
      });
      this.responsiveTableObserver.observe(content, {
        childList: true,
        subtree: true,
      });
    },
    scheduleResponsiveTableEnhance() {
      if (this.responsiveTableRefreshTimer) {
        window.clearTimeout(this.responsiveTableRefreshTimer);
      }

      this.responsiveTableRefreshTimer = window.setTimeout(() => {
        this.decorateResponsiveTables();
      }, 40);
    },
    decorateResponsiveTables() {
      const content = document.getElementById("content");
      if (!content) return;

      content.querySelectorAll("table").forEach((table) => {
        this.decorateResponsiveTable(table);
      });
    },
    decorateResponsiveTable(table) {
      if (!(table instanceof HTMLTableElement)) return;

      const headerRow =
        table.tHead?.rows?.[0] ||
        Array.from(table.rows).find((row) => row.querySelector("th"));
      const headerCells = headerRow ? Array.from(headerRow.cells) : [];
      const labels = [];

      headerCells.forEach((cell) => {
        const label = this.responsiveHeaderLabel(cell) || "Item";
        const colspan = Number(cell.getAttribute("colspan")) || 1;

        for (let index = 0; index < colspan; index += 1) {
          labels.push(label);
        }
      });

      table.classList.add("responsive-card-table");
      table.closest(".table-wrap")?.classList.add("responsive-card-table-wrap");

      if (headerRow && headerRow.closest("tbody")) {
        headerRow.dataset.responsiveHeader = "true";
      }

      Array.from(table.tBodies).forEach((tbody) => {
        Array.from(tbody.rows).forEach((row) => {
          if (row === headerRow && !table.tHead) {
            row.dataset.responsiveHeader = "true";
            return;
          }

          const cells = Array.from(row.cells);
          if (
            cells.length === 1 &&
            (Number(cells[0].getAttribute("colspan")) || 1) > 1
          ) {
            cells[0].dataset.label = "";
            cells[0].dataset.responsiveEmpty = "true";
            return;
          }

          let labelIndex = 0;
          cells.forEach((cell, cellIndex) => {
            const colspan = Number(cell.getAttribute("colspan")) || 1;

            if (
              !cell.dataset.label ||
              cell.dataset.autoResponsiveLabel === "true"
            ) {
              cell.dataset.label =
                labels[labelIndex] ||
                this.fallbackResponsiveTableLabel(cellIndex, cells.length);
              cell.dataset.autoResponsiveLabel = "true";
            }

            labelIndex += colspan;
          });
        });
      });
    },
    responsiveHeaderLabel(cell) {
      const clone = cell.cloneNode(true);
      clone
        .querySelectorAll("i, svg, .sort-icon")
        .forEach((element) => element.remove());
      return clone.textContent.replace(/\s+/g, " ").trim();
    },
    fallbackResponsiveTableLabel(index, total) {
      if (total === 1) return "";
      if (total === 2) return index === 0 ? "Field" : "Value";
      return `Column ${index + 1}`;
    },
  },
};
</script>

<style>
:root {
  --euroc-lock-open-blue: #075985;
  --euroc-lock-open-bg: #e0f2fe;
  --euroc-lock-closed-green: #166534;
  --euroc-lock-closed-bg: #dcfce7;
}

.status-pill.open:not(.gap-warning):not(.grade-gap-warning) {
  color: var(--euroc-lock-open-blue);
  background: var(--euroc-lock-open-bg);
  border-color: #bae6fd;
}

.status-pill.open:not(.gap-warning):not(.grade-gap-warning) i.fa-lock-open,
.metric-icon.lock-open i.fa-lock-open,
.scoring-lock-icon.open i.fa-lock-open {
  color: currentColor !important;
}

.status-pill.closed i.fa-lock,
.metric-icon.lock-closed i.fa-lock,
.scoring-lock-icon.closed i.fa-lock {
  color: var(--euroc-lock-closed-green) !important;
}

.btn-danger.action-btn i.fa-lock {
  color: #fff !important;
}

.modal-back-action {
  --bs-btn-color: #3152f5 !important;
  --bs-btn-bg: #eef4ff !important;
  --bs-btn-border-color: transparent !important;
  --bs-btn-hover-color: #2442d8 !important;
  --bs-btn-hover-bg: #dfe9ff !important;
  --bs-btn-hover-border-color: transparent !important;
  --bs-btn-active-color: #2442d8 !important;
  --bs-btn-active-bg: #d8e4ff !important;
  --bs-btn-active-border-color: transparent !important;
  width: 130px !important;
  height: 42px !important;
  min-height: 42px !important;
  border: 1px solid transparent !important;
  border-radius: 10px !important;
  padding: 0.55rem 0.9rem !important;
  display: inline-flex !important;
  gap: 0.4rem !important;
  align-items: center !important;
  justify-content: center !important;
  color: #3152f5 !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  white-space: nowrap !important;
  background: #eef4ff !important;
  background-color: #eef4ff !important;
  box-shadow: none !important;
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}

.modal-back-action:hover,
.modal-back-action:focus {
  border-color: transparent !important;
  color: #2442d8 !important;
  background: #dfe9ff !important;
  background-color: #dfe9ff !important;
}

.modal-back-action:hover {
  transform: translateY(-1px);
}

.modal-back-action:disabled {
  cursor: not-allowed !important;
  opacity: 0.65 !important;
  transform: none !important;
}

.modal-icon-dismiss-action {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  min-height: 36px !important;
  border: 1px solid #64748b !important;
  border-radius: 999px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: #fff !important;
  font-size: 1rem !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  background: #64748b !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transition: background 0.16s ease, border-color 0.16s ease, transform 0.16s ease;
}

.modal-icon-dismiss-action:hover,
.modal-icon-dismiss-action:focus {
  border-color: #475569 !important;
  color: #fff !important;
  background: #475569 !important;
  filter: none !important;
  opacity: 1 !important;
}

.modal-icon-dismiss-action:hover {
  transform: translateY(-1px);
}

.modal-icon-dismiss-action span {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 1 !important;
}

.floating-action-swap-enter-active,
.floating-action-swap-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.floating-action-swap-enter-from,
.floating-action-swap-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.86);
}

.floating-action-swap-enter-to,
.floating-action-swap-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (max-width: 768px) {
  #content .admin-dashboard,
  #content .tr-dashboard,
  #content .tr-scoring,
  #content .statistics-page,
  #content .frr-settings,
  #content .scoring-dashboard,
  #content .settings-form-page {
    width: 100% !important;
    max-width: 100% !important;
    margin-top: 1rem !important;
    margin-bottom: 1.25rem !important;
    padding-right: max(0.85rem, env(safe-area-inset-right)) !important;
    padding-left: max(0.85rem, env(safe-area-inset-left)) !important;
  }

  #content .dashboard-title-row,
  #content .toolbar,
  #content .copy-card,
  #content .admin-generation,
  #content .generation-card,
  #content .report-toolbar {
    align-items: stretch !important;
    flex-direction: column !important;
  }

  #content .dashboard-title-row {
    gap: 0.85rem !important;
  }

  #content .header-actions,
  #content .toolbar-actions,
  #content .generation-actions,
  #content .footer-action-row {
    width: 100% !important;
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(136px, 1fr)) !important;
    gap: 0.5rem !important;
  }

  #content .header-actions > .action-btn,
  #content .toolbar-actions > .action-btn,
  #content .generation-actions > .action-btn,
  #content .header-actions > .table-link,
  #content .toolbar-actions > .table-link,
  #content .header-actions > .scoring-table-btn,
  #content .toolbar-actions > .scoring-table-btn {
    width: 100% !important;
    min-width: 0 !important;
  }

  #content .toolbar .search-field,
  #content .search-field {
    flex: 0 0 auto !important;
    min-width: 0 !important;
    width: 100% !important;
  }

  #content .toolbar .search-input,
  #content .search-input,
  #content .search-input .form-control {
    min-width: 0 !important;
    width: 100% !important;
  }

  #content .toolbar-count {
    align-self: flex-start !important;
    white-space: normal !important;
  }

  #content .table-wrap {
    max-width: 100% !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch;
  }

  #content .table-wrap:not(.mobile-card-table) .table,
  #content .table-wrap:not(.mobile-card-table) .project-table,
  #content .wide-table .table {
    min-width: 720px !important;
  }

  #content .scroll-controls {
    top: auto !important;
    right: 0.85rem !important;
    bottom: calc(1rem + env(safe-area-inset-bottom)) !important;
    transform: none !important;
  }
}

@media (max-width: 576px) {
  #content .admin-dashboard,
  #content .tr-dashboard,
  #content .tr-scoring,
  #content .statistics-page,
  #content .frr-settings,
  #content .scoring-dashboard,
  #content .settings-form-page {
    padding-right: max(0.75rem, env(safe-area-inset-right)) !important;
    padding-left: max(0.75rem, env(safe-area-inset-left)) !important;
  }

  #content .dashboard-header {
    margin-bottom: 1rem !important;
    padding-bottom: 0.85rem !important;
  }

  #content .dashboard-title-row h1,
  #content .dashboard-title-row h2 {
    font-size: 1.72rem !important;
    line-height: 1.06 !important;
  }

  #content .dashboard-subtitle {
    font-size: 0.88rem !important;
  }

  #content .eyebrow {
    margin-bottom: 0.25rem !important;
    font-size: 0.68rem !important;
  }

  #content .toolbar {
    gap: 0.75rem !important;
    margin-bottom: 0.85rem !important;
    padding: 0.85rem !important;
    border-radius: 12px !important;
  }

  #content .action-btn,
  #content .table-link,
  #content .scoring-table-btn {
    min-height: 44px !important;
    min-width: 0 !important;
    padding-right: 0.75rem !important;
    padding-left: 0.75rem !important;
  }

  #content .responsive-card-table-wrap {
    overflow: visible !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  #content .table-wrap:not(.mobile-card-table) table.responsive-card-table,
  #content .wide-table table.responsive-card-table,
  #content table.responsive-card-table {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
    background: transparent !important;
  }

  #content table.responsive-card-table thead,
  #content table.responsive-card-table tr[data-responsive-header="true"] {
    display: none !important;
  }

  #content table.responsive-card-table tbody,
  #content table.responsive-card-table tfoot {
    display: grid !important;
    width: 100% !important;
    gap: 0.75rem !important;
  }

  #content table.responsive-card-table tr {
    display: grid !important;
    width: 100% !important;
    gap: 0.55rem !important;
    border: 1px solid #dbe3ef !important;
    border-radius: 14px !important;
    padding: 0.9rem !important;
    background: #fff !important;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08) !important;
  }

  #content table.responsive-card-table tr:hover td,
  #content table.responsive-card-table tr:hover th {
    background-color: inherit;
  }

  #content table.responsive-card-table td,
  #content table.responsive-card-table th {
    width: 100% !important;
    min-height: 34px !important;
    border: 0 !important;
    padding: 0.18rem 0 !important;
    display: grid !important;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr) !important;
    gap: 0.75rem !important;
    align-items: center !important;
    color: #1f2937;
    font-size: 0.92rem !important;
    text-align: right !important;
    white-space: normal !important;
  }

  #content table.responsive-card-table td::before,
  #content table.responsive-card-table th::before {
    content: attr(data-label);
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1.12;
    text-align: left;
    text-transform: uppercase;
  }

  #content table.responsive-card-table td[data-label=""],
  #content table.responsive-card-table th[data-label=""],
  #content table.responsive-card-table [data-responsive-empty="true"] {
    display: block !important;
    text-align: center !important;
  }

  #content table.responsive-card-table td[data-label=""]::before,
  #content table.responsive-card-table th[data-label=""]::before,
  #content table.responsive-card-table [data-responsive-empty="true"]::before {
    content: none !important;
  }

  #content table.responsive-card-table td[colspan],
  #content table.responsive-card-table th[colspan] {
    grid-template-columns: 1fr !important;
  }

  #content table.responsive-card-table .text-center,
  #content table.responsive-card-table .text-start,
  #content table.responsive-card-table .text-end {
    text-align: right !important;
  }

  #content table.responsive-card-table .d-flex,
  #content table.responsive-card-table .action-stack,
  #content table.responsive-card-table .table-actions,
  #content table.responsive-card-table .btn-group,
  #content table.responsive-card-table .form-check {
    justify-content: flex-end !important;
  }

  #content table.responsive-card-table select,
  #content table.responsive-card-table input,
  #content table.responsive-card-table textarea,
  #content table.responsive-card-table .form-select,
  #content table.responsive-card-table .form-control {
    max-width: 100% !important;
  }

  html[data-theme="dark"] #content table.responsive-card-table tr {
    border-color: rgba(148, 163, 184, 0.24) !important;
    background: #202833 !important;
    box-shadow: 0 12px 26px rgba(0, 0, 0, 0.24) !important;
  }

  html[data-theme="dark"] #content table.responsive-card-table td,
  html[data-theme="dark"] #content table.responsive-card-table th {
    color: #e5edf7;
  }

  html[data-theme="dark"] #content table.responsive-card-table td::before,
  html[data-theme="dark"] #content table.responsive-card-table th::before {
    color: #9fb1c8;
  }

  #content .icon-action {
    min-width: 38px !important;
    min-height: 38px !important;
  }

  #content .sep {
    min-height: 145px !important;
  }
}
</style>
